/**
 * Internationalization Utility
 * 
 * Provides functions for translating text based on the current language.
 */

import { en, fr, de, it } from './translations';

/**
 * Get a translation by key for the specified language
 * @param {string} key The translation key
 * @param {string} language The language code (default: 'en')
 * @param {Object} placeholders Optional placeholder values to inject into translation
 * @returns {string} The translated text
 */
export function getTranslation(key, language = 'en', placeholders = {}) {
  // Get the translations for the specified language
  const translations = getTranslationsForLanguage(language);
  
  // Get the translation by key or fallback to English or the key itself
  let translation = translations[key] || en[key] || key;
  
  // Replace placeholders if provided
  if (placeholders && Object.keys(placeholders).length > 0) {
    Object.keys(placeholders).forEach(placeholder => {
      const regex = new RegExp(`{{${placeholder}}}`, 'g');
      translation = translation.replace(regex, placeholders[placeholder]);
    });
  }
  
  return translation;
}

/**
 * Get translations for the specified language
 * @param {string} language The language code
 * @returns {Object} The translations
 */
function getTranslationsForLanguage(language) {
  switch (language) {
    case 'fr':
      return fr;
    case 'de':
      return de;
    case 'it':
      return it;
    case 'en':
    default:
      return en;
  }
}

/**
 * Format a number according to the specified language
 * @param {number} number The number to format
 * @param {string} language The language code
 * @param {Object} options Formatting options
 * @returns {string} The formatted number
 */
export function formatNumber(number, language = 'en', options = {}) {
  const formatter = new Intl.NumberFormat(getLocaleForLanguage(language), options);
  return formatter.format(number);
}

/**
 * Format a currency value according to the specified language and currency
 * @param {number} amount The amount to format
 * @param {string} currency The currency code (default: 'CHF')
 * @param {string} language The language code
 * @returns {string} The formatted currency value
 */
export function formatCurrency(amount, currency = 'CHF', language = 'en') {
  return formatNumber(amount, language, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  });
}

/**
 * Format a date according to the specified language
 * @param {Date|string} date The date to format
 * @param {string} language The language code
 * @param {Object} options Formatting options
 * @returns {string} The formatted date
 */
export function formatDate(date, language = 'en', options = {}) {
  const dateObj = date instanceof Date ? date : new Date(date);
  const formatter = new Intl.DateTimeFormat(getLocaleForLanguage(language), {
    dateStyle: 'medium',
    ...options
  });
  return formatter.format(dateObj);
}

/**
 * Get the locale for the specified language
 * @param {string} language The language code
 * @returns {string} The locale
 */
function getLocaleForLanguage(language) {
  switch (language) {
    case 'fr':
      return 'fr-CH';
    case 'de':
      return 'de-CH';
    case 'it':
      return 'it-CH';
    case 'en':
    default:
      return 'en-US';
  }
}

/**
 * Apply translations to all elements with data-i18n attributes
 * @param {string} language The language code
 * @param {HTMLElement} container Optional container element to limit scope
 */
export function applyTranslations(language = 'en', container = document) {
  const elements = container.querySelectorAll('[data-i18n]');
  
  elements.forEach(element => {
    const key = element.getAttribute('data-i18n');
    const type = element.getAttribute('data-i18n-type') || 'text';
    
    if (type === 'text') {
      element.textContent = getTranslation(key, language);
    } else if (type === 'placeholder') {
      element.placeholder = getTranslation(key, language);
    } else if (type === 'title') {
      element.title = getTranslation(key, language);
    } else if (type === 'aria-label') {
      element.setAttribute('aria-label', getTranslation(key, language));
    }
  });
}